
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { FaUsers, FaGlobe, FaStar, FaHandsHelping } from "react-icons/fa";
import ApiCallService from "../../Service/ApiCallService";
import Spinner from "../common/Spinner";
import { useSelector } from "react-redux";
import { useTheme, useMediaQuery, Typography, Button, Box } from "@mui/material";
import ReadMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FollowUpHistoryView  from "./FollowUpHistoryView";
import HorizontalJourneyView from "./HorizontalJourneyView";
import { usePermission } from "../../permissions/usePermission";

const iconMap = {
  "National President": <FaStar />,
  "State President": <FaGlobe />,
  "Immediate Past State President": <FaGlobe />,
  "Chapter President": <FaUsers />,
  "NEC Member": <FaStar />,
  Volunteer: <FaHandsHelping />,
  default: <FaStar />,
};

const getJourneyIcon = (position) => {
  const normalizedPosition = position?.trim();
  if (!normalizedPosition) return iconMap.default;

  const directMatch = iconMap[normalizedPosition];
  if (directMatch) return directMatch;

  const lowered = normalizedPosition.toLowerCase();
  if (lowered.includes("state president")) return iconMap["State President"];
  if (lowered.includes("chapter president"))
    return iconMap["Chapter President"];
  if (lowered.includes("volunteer")) return iconMap.Volunteer;
  if (lowered.includes("nec member")) return iconMap["NEC Member"];
  if (lowered.includes("national president"))
    return iconMap["National President"];

  return iconMap.default;
};

const formatDate = (date) => (date ? new Date(date).getFullYear() : "Present");

const EmptyJourneyState = ({ onAdd }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
        className="text-6xl mb-4"
      >
        🌱
      </motion.div>

      <h3 className="text-xl font-semibold text-gray-700 mb-2">
        No journey found
      </h3>

      <p className="text-gray-500 mb-6 max-w-sm">
        You haven’t added any journey yet. Add your first role or experience to
        get started.
      </p>

    </motion.div>
  );
};

const FullJourneyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canByKey } = usePermission();
  const loggedInUserId = localStorage.getItem("userId");
  const queryParams = new URLSearchParams(location.search);
  const preloadedJourney = Array.isArray(location.state?.journeyData)
    ? location.state.journeyData
    : null;
  const targetUserId =
    location.state?.userId ||
    location.state?.profileUserId ||
    queryParams.get("userId") ||
    loggedInUserId;
  const isSelfJourney =
    targetUserId && loggedInUserId
      ? String(targetUserId) === String(loggedInUserId)
      : Boolean(loggedInUserId);
  const canCreateJourney =
    isSelfJourney || canByKey("USER_MANAGEMENT_JOURNEY_CREATE");
  const [userData, setUserData] = useState();
  const [profile, setProfile] = useState({
    userImageURL: "",
    userFirstName: "",
    userMiddleName: "",
    userLastName: "",
    emailId: "",
    userRole: "",
    journey: [],
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(true);
  const layoutFromState = location.state?.layout;
  const layoutFromQuery = new URLSearchParams(location.search).get("layout");
  const layoutMode = layoutFromState || layoutFromQuery || "default";
  const isHorizontal = layoutMode === "horizontal";
  const MAX_LENGTH = 150;
  const [expandedIndex, setExpandedIndex] = useState(null);

  useEffect(() => {
    window.scrollTo({top:0, behavior:"smooth"})
  },[])

  useEffect(() => {
    const fetchJourney = async () => {
      if (preloadedJourney) {
        setUserData(preloadedJourney);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/${targetUserId}/journey`
        );

        setUserData(Array.isArray(res) ? res : []);

        const adaptedJourney = Array.isArray(res)
          ? res.map((item) => ({
            duration: `${formatDate(item.startDate)} - ${formatDate(
              item.endDate
            )}`,
            roles: [
              {
                position: item.position,
                description: item.description,
                icon: getJourneyIcon(item.position),
              },
            ],
          }))
          : [];

        setProfile((prev) => ({
          ...prev,
          journey: adaptedJourney,
        }));
      } catch (err) {
        console.error("Failed to fetch journey", err);
        setUserData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchJourney();
  }, [targetUserId, preloadedJourney]);

  const formatDateForInput = (date, fallback = "") => {
    if (!date) return fallback;
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const renderJourneyView = () => {
    switch (layoutMode) {
      case "horizontal":
        return (
          <HorizontalJourneyView
            entries={userData}
            onSelect={(entry) =>
              navigate(
                `/journey/edit/${entry._id}?mode=view&userId=${targetUserId}`
              )
            }
            formatDateForInput={formatDateForInput}
            iconMap={iconMap}
            getJourneyIcon={getJourneyIcon}
            expandedIndex={expandedIndex}
            setExpandedIndex={setExpandedIndex}
            maxLength={225}
          />
        );

      case "vertical":
        return (
          <FollowUpHistoryView 
            entries={userData}
            onSelect={(entry) =>
              navigate(
                `/journey/edit/${entry._id}?mode=view&userId=${targetUserId}`
              )
            }
            formatDateForInput={formatDateForInput}
          />
        );

      default:
        return (
          <>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              {!loading && userData?.length !== 0 && (
                <Box
                  component={motion.div}
                  initial={{ height: 0 }}
                  animate={{ height: '100%' }}
                  transition={{ duration: 2, ease: 'easeInOut' }}
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: 0,
                    transform: 'translateX(-50%)',
                    width: 1,
                    height: '100%',
                  }}
                />
              )}

              {userData?.length > 0 &&
                userData.map((entry, index) => (
                  <Box
                    component={motion.div}
                    onClick={() =>
                      navigate(`/journey/edit/${entry._id}?mode=view&userId=${targetUserId}`)
                    }
                    key={index}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.25 }}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      maxWidth: { md: '50%' },
                      mb: 2,
                      alignSelf: index % 2 === 0 ? 'flex-start' : 'flex-end',
                      pr: index % 2 === 0 ? { md: 5 } : 0,
                      pl: index % 2 !== 0 ? { md: 5 } : 0,
                      textAlign: 'left',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        mb: 1,
                      }}
                    >
                      <Typography component="p" sx={{ fontSize: 13 }}>
                        {formatDateForInput(entry.startDate)}
                      </Typography>
                      <Typography component="p" sx={{ fontSize: 13 }}>
                        {formatDateForInput(entry.endDate, 'Present')}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        position: 'absolute',
                        top: 40,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 24,
                        height: 24,
                        bgcolor: 'primary.main',
                        borderRadius: '50%',
                        border: '4px solid',
                        borderColor: 'background.paper',
                        boxShadow: 3,
                      }}
                    />

                    <Box sx={{ mt: 2, width: '100%' }}>
                      <Box
                        component={motion.div}
                        whileHover={{ scale: 1.05 }}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          p: { xs: 2.5, sm: 3 },
                          bgcolor: 'grey.50',
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          boxShadow: 1,
                        }}
                      >
                        <Box
                          component={motion.div}
                          whileHover={{ scale: 1.3, rotate: 10 }}
                          transition={{ type: 'spring', stiffness: 200 }}
                          sx={{
                            color: 'primary.main',
                            fontSize: 28,
                            flexShrink: 0,
                            lineHeight: 1,
                          }}
                        >
                          {getJourneyIcon(entry.position)}
                        </Box>

                        <Box sx={{ width: '100%' }}>
                          <Typography
                            component="h3"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: 18, sm: 20 },
                              color: 'text.primary',
                              mb: 1,
                            }}
                          >
                            {entry.position}
                          </Typography>

                          <Box
                            sx={{
                              mt: 1,
                              color: 'text.secondary',
                              fontSize: 14,
                              lineHeight: 1.7,
                            }}
                          >
                            <Box
                              component="div"
                              dangerouslySetInnerHTML={{
                                __html:
                                  expandedIndex === index ||
                                    entry.description.length <= MAX_LENGTH
                                    ? entry.description
                                    : entry.description.slice(0, MAX_LENGTH) + '...',
                              }}
                            />

                            {entry.description.length > MAX_LENGTH &&
                              expandedIndex !== index && (
                                <Typography
                                  component="span"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedIndex(index);
                                  }}
                                  sx={{
                                    ml: 1,
                                    cursor: 'pointer',
                                    fontWeight: 700,
                                    fontSize: 14,
                                    color: 'primary.main',
                                    '&:hover': { color: 'primary.dark' },
                                  }}
                                >
                                  Read More
                                </Typography>
                              )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
            </Box>
          </>
        );
    }
  };

  return (
    <Box
      sx={{
        maxWidth: { xs: '100%', sm: 900, md: 1100, xl: 1200 },
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3, md: 4 },
        bgcolor: '#fff',
        position: 'relative',
        width: '100%',
        borderRadius: 2,
      }}
    >
      <Spinner loading={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: {
              xs: "1.5rem", // h5-ish
              sm: "1.825rem", // h4-ish
              md: "2.05rem", // h4-ish
            },
            fontWeight: 700,
            color: 'text.primary',
            textAlign: { xs: 'center', sm: 'left', md: "center" },
            flex: 1,
          }}
        >
          Journey in BJS India
        </Typography>

        <Button
          variant="contained"
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.secondary.main,
            px: { xs: 1.5, sm: 3 },
            py: 1,
            minWidth: { xs: 'auto', sm: 120 },
            alignSelf: { xs: 'flex-end', sm: 'center' },
            wordSpacing: '3px',
          }}
          onClick={() => canCreateJourney && navigate(`/journey/create`)}
          disabled={!canCreateJourney}
        >
          {isMobile ? <AddCircleOutlineIcon /> : 'Add Journey'}
        </Button>
      </Box>

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 1, sm: 4 },
          py: { xs: 4, sm: 6 },
          width: '100%',
        }}
      >
        {!loading && userData?.length === 0 && (
          <EmptyJourneyState onAdd={() => navigate(`/journey/create`)} />
        )}

        {/* {isHorizontal ? (
          <HorizontalJourneyView
            entries={userData}
            onSelect={(entry) =>
              navigate(`/journey/edit/${entry._id}?mode=view&userId=${targetUserId}`)
            }
            formatDateForInput={formatDateForInput}
            iconMap={iconMap}
            getJourneyIcon={getJourneyIcon}
            expandedIndex={expandedIndex}
            setExpandedIndex={setExpandedIndex}
            maxLength={isHorizontal ? 90 : MAX_LENGTH}
          />
        ) : (
        )} */}
        {renderJourneyView()}
      </Box>
    </Box>
  );
};

export default FullJourneyPage;
